// CabecalhoAcervo.js — a abertura da página /livros.
//
// ================= O QUE FALTAVA (16/09/2026) =================
//
// A página do catálogo começava assim: um caminho de migalhas, a barra do
// Clube e, logo em seguida, uma faixa cinza escrito "Resultados". Era a tela
// mais visitada do site e a única sem NOME — literalmente: não havia um <h1>
// em lugar nenhum dela. Quem chegava pelo buscador caía numa grade de capas
// sem nunca ler o que era aquilo.
//
// E havia um segundo buraco, maior: as CATEGORIAS moram num popup desde
// 11/09/2026. A decisão foi certa (a coluna fixa de 260px comia a grade), mas
// ela teve um custo que nunca foi pago: um visitante que não clica no botão
// não descobre que a editora publica em vinte e tantas áreas. O acervo tinha
// virado uma pilha sem índice.
//
// Este cabeçalho resolve os dois com a mesma peça:
//
//   1. UM NOME, com o tamanho do acervo dito em números que saem da própria
//      lista — nada digitado à mão;
//   2. AS ÁREAS À VISTA, como atalhos, já ordenadas por quantos livros têm.
//
// POR QUE ELE NÃO GRUDA NO TOPO: a faixa de "Resultados" gruda, e ela carrega
// a busca, o botão de Categorias e os chips do que está filtrado — o que a
// pessoa precisa NO MEIO da rolagem. Este cabeçalho é a apresentação, e
// apresentação se lê uma vez. Empilhar os dois no alto comeria um terço da tela
// justamente onde ficam as capas.
import React from "react";
import { Ornamento } from "../../ui/TituloDeSecao";

/** Quantos atalhos de área aparecem antes do "ver todas". */
const ATALHOS_VISIVEIS = 10;

/**
 * As áreas com mais livros, já com a contagem.
 *
 * A ORDEM É POR QUANTIDADE, e não alfabética: a lista existe para mostrar do
 * que a editora é feita, e a ordem alfabética começaria por qualquer coisa que
 * tenha um livro só. Quem procura uma área específica tem a busca do painel,
 * que é ordenada por nome.
 */
const maioresAreas = (categorias, contagem, limite) =>
  categorias
    .map((nome) => ({ nome, total: contagem.get(nome) || 0 }))
    .filter((area) => area.total > 0)
    .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, "pt-BR"))
    .slice(0, limite);

const Atalho = ({ ativo, onClick, children, titulo }) => (
  <button
    type="button"
    onClick={onClick}
    title={titulo}
    aria-pressed={ativo}
    className={
      "foco-laranja inline-flex max-w-full items-center gap-2 rounded-controle border-2 " +
      "px-3 py-1.5 text-sm font-semibold transition-[transform,box-shadow,background-color,border-color,color] duration-200 " +
      (ativo
        ? "border-insigne-orange-dark bg-insigne-orange text-paper shadow-[3px_3px_0_0_rgb(var(--c-orange-dark))]"
        : "border-insigne-sombra bg-insigne-navy-soft text-white/85 shadow-[3px_3px_0_0_rgb(var(--c-sombra))] " +
          "hover:-translate-x-[2px] hover:-translate-y-[2px] hover:text-white hover:shadow-[5px_5px_0_0_rgb(var(--c-sombra))]")
    }
  >
    {children}
  </button>
);

/** O número dentro do atalho. Cor própria para não competir com o nome. */
const Contagem = ({ n, ativo }) => (
  <span
    className={
      "shrink-0 text-xs font-bold tabular-nums " + (ativo ? "text-paper/70" : "text-white/40")
    }
  >
    {n}
  </span>
);

/**
 * DOIS TOTAIS, e eles respondem perguntas diferentes.
 *
 * `totalDeLivros` / `totalDeAreas` / `totalDeAutores` descrevem O ACERVO, e não
 * mudam quando alguém busca por uma palavra: são uma frase sobre a editora.
 *
 * `totalVisivel` e as contagens das áreas descrevem O QUE ESTÁ NA TELA AGORA —
 * já com a busca aplicada, porque é o número que responde "quantos livros eu
 * veria se clicasse aqui". Misturar os dois faria a etiqueta de uma área
 * prometer quarenta livros e a grade mostrar três.
 */
export default function CabecalhoAcervo({
  totalDeLivros,
  totalDeAreas,
  totalDeAutores,
  totalVisivel,
  categorias,
  contagemPorCategoria,
  categoriaSel,
  aoEscolherCategoria,
  aoVerTodasAsAreas,
  carregando,
}) {
  const areas = maioresAreas(categorias, contagemPorCategoria, ATALHOS_VISIVEIS);
  // As áreas que ainda TÊM livro no recorte atual. Com uma busca ligada, metade
  // das categorias fica em zero e some dos atalhos; dizer "ver todas as 24
  // áreas" ali seria oferecer um painel que não tem 24 linhas para mostrar.
  const areasComLivros = categorias.filter(
    (nome) => (contagemPorCategoria.get(nome) || 0) > 0
  ).length;
  const sobram = Math.max(0, areasComLivros - areas.length);

  return (
    <header className="mb-8">
      <span className="chapeu">Acervo da editora</span>

      {/* O <h1> QUE NÃO EXISTIA. Além de ser o que orienta quem chega pelo
          buscador, ele é o começo da hierarquia de títulos da página: a faixa
          de Resultados usa <h2> e o estado vazio usa <h3>, e os dois estavam
          pendurados no nada. */}
      {/* (18/10/2026) Na League Gothic, com o losango embaixo: o desenho dos
          títulos do site do Congresso e do site principal. */}
      <h1 className="titulo-display mb-0 mt-[0.6rem] text-[2.8rem] text-white sm:text-[3.6rem]">
        Todo o <span className="text-insigne-orange">catálogo</span> da Insigne Acadêmica
      </h1>
      <Ornamento className="mt-[0.9rem]" />

      {/* A LINHA DE NÚMEROS SÓ APARECE COM A LISTA NA MÃO. Enquanto o catálogo
          carrega ela seria "0 livros em 0 áreas", que é pior do que nada — e
          ela também não pode piscar de 0 para 199, empurrando a grade. */}
      {!carregando && totalDeLivros > 0 && (
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-[15px]">
          <strong className="font-bold text-white">{totalDeLivros}</strong> livros publicados
          {totalDeAreas > 0 && (
            <>
              {" "}em <strong className="font-bold text-white">{totalDeAreas}</strong> áreas
            </>
          )}
          {totalDeAutores > 0 && (
            <>
              , de <strong className="font-bold text-white">{totalDeAutores}</strong> autores
            </>
          )}
          . Cada capa abre a página do livro, com prévia, avaliações e as duas versões
          (digital e impressa), quando o título tem as duas.
        </p>
      )}

      {/* ===== OS ATALHOS DE ÁREA =====
          Eles fazem o que o popup fazia sozinho até hoje, com uma diferença que
          é o ponto: aparecem sem ninguém pedir. O popup continua existindo — é
          ele que tem a lista inteira, a busca por nome e o que acompanha a
          rolagem — e é para lá que o "ver todas" manda.

          `Todos` é um atalho como os outros, e não um botão de limpar: ele é o
          estado em que a página nasce, e mostrá-lo marcado é o que explica ao
          visitante que ele está vendo o acervo inteiro. */}
      {!carregando && areas.length > 0 && (
        <nav className="mt-6" aria-label="Áreas do acervo">
          <div className="flex flex-wrap items-center gap-2">
            <Atalho ativo={!categoriaSel} onClick={() => aoEscolherCategoria("")}>
              Todos
              <Contagem n={totalVisivel} ativo={!categoriaSel} />
            </Atalho>

            {areas.map((area) => {
              const ativo = categoriaSel === area.nome;
              return (
                <Atalho
                  key={area.nome}
                  ativo={ativo}
                  titulo={area.nome}
                  // Clicar na área já marcada volta para o acervo inteiro: o
                  // atalho é o mesmo lugar onde a pessoa ligou o filtro, e é o
                  // primeiro lugar onde ela tenta desligá-lo.
                  onClick={() => aoEscolherCategoria(ativo ? "" : area.nome)}
                >
                  {/* `truncate` com `min-w-0`: nomes de categoria deste acervo
                      chegam a sessenta caracteres ("Congresso de Direito
                      Aplicado - Org. Insigne Acadêmica") e um deles sozinho
                      ocuparia a fileira inteira. */}
                  <span className="min-w-0 truncate">{area.nome}</span>
                  <Contagem n={area.total} ativo={ativo} />
                </Atalho>
              );
            })}

            {sobram > 0 && (
              <button
                type="button"
                onClick={aoVerTodasAsAreas}
                className="foco-laranja inline-flex items-center gap-1.5 rounded-controle px-2 py-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-insigne-orange"
              >
                ver todas as {areasComLivros} áreas
                <i className="bi bi-arrow-right text-xs" aria-hidden="true" />
              </button>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
